import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Loader2, Package, Check, AlertTriangle, ArrowDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PullFromStockModal = ({ open, onOpenChange, order, partDetails, onSuccess, user }) => {
  const [step, setStep] = useState('select'); // 'select' | 'confirm'
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [barsToBook, setBarsToBook] = useState('');
  const [barsRequired, setBarsRequired] = useState(null);
  const [barsAlreadyBooked, setBarsAlreadyBooked] = useState(0);

  const requiredGrade = partDetails?.material_grade || '';
  const requiredDiameter = partDetails?.diameter || 0;

  const fetchMatchingInventory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory`);
      const all = res.data || [];
      // Filter to matching grade items, sorted by exact match first
      const matching = all
        .filter(item => item.quantity_available > 0)
        .map(item => {
          const gradeMatch = requiredGrade && item.material_grade
            ? item.material_grade.trim().toLowerCase() === requiredGrade.trim().toLowerCase()
            : false;
          return { ...item, gradeMatch };
        })
        .sort((a, b) => {
          // Exact grade match first, then by available qty desc
          if (a.gradeMatch !== b.gradeMatch) return b.gradeMatch - a.gradeMatch;
          return b.quantity_available - a.quantity_available;
        });
      setInventory(matching);
    } catch (err) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [requiredGrade]);

  useEffect(() => {
    if (open && order) {
      setStep('select');
      setSelectedItem(null);
      setBarsToBook('');
      fetchMatchingInventory();
      // Fetch bars required and already booked
      axios.get(`${BACKEND_URL}/api/material-allocations/eligible-orders`, {
        params: { inventory_id: '' }
      }).then(res => {
        const match = (res.data || []).find(o => o.order_number === order.order_number);
        if (match) {
          setBarsRequired(match.bars_required);
          setBarsAlreadyBooked(match.total_bars_already_booked || 0);
        } else {
          // Fallback: use order quantity as bars required
          setBarsRequired(order.quantity);
          setBarsAlreadyBooked(0);
        }
      }).catch(() => {
        setBarsRequired(order.quantity);
      });
      // Also fetch existing allocations for accurate booked count
      axios.get(`${BACKEND_URL}/api/material-allocations/by-order/${order.order_number}`).then(res => {
        setBarsAlreadyBooked(res.data.total_active_bars || 0);
      }).catch(() => {});
    }
  }, [open, order, fetchMatchingInventory]);

  const handleConfirmStep = () => {
    const qty = parseInt(barsToBook);
    if (!selectedItem) {
      toast.error('Please select a raw material item');
      return;
    }
    if (!qty || qty <= 0) {
      toast.error('Enter a valid positive number of bars');
      return;
    }
    if (qty > selectedItem.quantity_available) {
      toast.error(`Cannot book more than ${selectedItem.quantity_available} available bars`);
      return;
    }
    setStep('confirm');
  };

  const handleBook = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/material-allocations/book`, {
        inventory_id: selectedItem.inventory_id,
        order_number: order.order_number,
        bars_booked: parseInt(barsToBook),
        booked_by: user?.email || 'System'
      });
      toast.success(res.data.message);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-green-500/30 max-w-2xl max-h-[85vh] flex flex-col" data-testid="pull-from-stock-modal">
        <DialogHeader>
          <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
            <ArrowDown className="w-5 h-5 text-green-400" />
            {step === 'select' ? 'Pull from Stock' : 'Confirm Allocation'}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            {step === 'select'
              ? `Allocate raw material to ${order.order_number}`
              : 'Review and confirm the material allocation'}
          </DialogDescription>
        </DialogHeader>

        {/* WO Info Banner */}
        <div className="bg-slate-800/80 border border-slate-700 rounded-lg p-3" data-testid="wo-info-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Works Order</p>
                <p className="text-white font-mono font-bold">{order.order_number}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Customer</p>
                <p className="text-white text-sm">{order.customer}</p>
              </div>
              {requiredGrade && (
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Required Grade</p>
                  <p className="text-amber-400 font-bold">{requiredGrade}</p>
                </div>
              )}
              {requiredDiameter > 0 && (
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Diameter</p>
                  <p className="text-cyan-400 font-mono">{requiredDiameter} mm</p>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-500 uppercase">Order Qty</p>
              <p className="text-lg font-bold text-white font-mono">{order.quantity} <span className="text-xs text-slate-500">pcs</span></p>
              {barsRequired != null && (
                <>
                  <p className="text-[10px] text-slate-500 uppercase mt-1">Bars Required</p>
                  <p className="text-2xl font-bold text-amber-400 font-mono" data-testid="bars-required-display">{barsRequired}</p>
                </>
              )}
              {barsAlreadyBooked > 0 && (
                <p className="text-xs text-cyan-400">{barsAlreadyBooked} booked{barsRequired != null && <> — <span className="text-white font-bold">{Math.max(0, barsRequired - barsAlreadyBooked)} still needed</span></>}</p>
              )}
            </div>
          </div>
        </div>

        {step === 'select' && (
          <div className="flex flex-col gap-4 overflow-hidden flex-1 min-h-0">
            {/* Available Stock List */}
            <div className="flex-1 overflow-y-auto border border-slate-700 rounded-lg min-h-[200px] max-h-[300px]" data-testid="stock-list">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-green-400" />
                </div>
              ) : inventory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <AlertTriangle className="w-8 h-8 mb-2 text-amber-500/50" />
                  <p className="text-sm">No raw material with available stock found</p>
                </div>
              ) : (
                inventory.map((item) => (
                  <div
                    key={item.inventory_id}
                    onClick={() => setSelectedItem(item)}
                    className={`p-3 border-b border-slate-800 cursor-pointer transition-colors ${
                      selectedItem?.inventory_id === item.inventory_id
                        ? 'bg-green-500/10 border-l-2 border-l-green-400'
                        : 'hover:bg-slate-800/50'
                    } ${!item.gradeMatch ? 'opacity-50' : ''}`}
                    data-testid={`stock-option-${item.inventory_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-bold text-sm">{item.material_grade}</p>
                            {item.gradeMatch ? (
                              <span className="text-[10px] px-1.5 py-0.5 bg-green-950 text-green-400 rounded">Match</span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 bg-red-950 text-red-400 rounded">Different Grade</span>
                            )}
                          </div>
                          <p className="text-slate-500 text-xs font-mono">{item.inventory_id}</p>
                        </div>
                        <div>
                          <p className="text-cyan-400 font-mono text-sm">{item.diameter} mm</p>
                          {item.length > 0 && <p className="text-slate-500 text-xs">L: {item.length} mm</p>}
                        </div>
                        <div>
                          <p className="text-purple-400 font-mono text-sm">{item.cast_number || 'No Cast #'}</p>
                          <p className="text-slate-500 text-xs">{item.heat_treatment_condition}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-400 font-mono">{item.quantity_available}</p>
                        <p className="text-[10px] text-slate-500">{item.unit || 'bars'} available</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Quantity Input */}
            {selectedItem && (
              <div className="flex items-end gap-4" data-testid="bars-input-section">
                <div className="flex-1">
                  <Label className="text-slate-400 uppercase text-xs">Number of Bars to Pull</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedItem.quantity_available}
                    step="1"
                    value={barsToBook}
                    onChange={(e) => setBarsToBook(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-lg font-mono text-center"
                    placeholder="Enter quantity..."
                    data-testid="pull-bars-input"
                  />
                </div>
                <Button
                  onClick={handleConfirmStep}
                  disabled={!barsToBook || parseInt(barsToBook) <= 0}
                  className="bg-green-600 hover:bg-green-500 h-10"
                  data-testid="pull-review-btn"
                >
                  Review <Check className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 'confirm' && selectedItem && (
          <div className="space-y-4">
            <div className="bg-green-950/30 border border-green-500/20 rounded-lg p-4 space-y-3" data-testid="pull-confirm-summary">
              <h4 className="text-green-400 font-bold uppercase text-xs tracking-wider">Allocation Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Material Grade</p>
                  <p className="text-white font-bold">{selectedItem.material_grade}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Cast / Heat Number</p>
                  <p className="text-purple-400 font-mono font-bold">{selectedItem.cast_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Diameter</p>
                  <p className="text-cyan-400 font-mono">{selectedItem.diameter} mm</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Condition</p>
                  <p className="text-white">{selectedItem.heat_treatment_condition}</p>
                </div>
              </div>
              <div className="border-t border-green-500/20 pt-3 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Available Now</p>
                  <p className="text-white font-mono font-bold text-lg">{selectedItem.quantity_available}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Bars to Pull</p>
                  <p className="text-green-400 font-mono font-bold text-xl">{barsToBook}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Remaining After</p>
                  <p className={`font-mono font-bold text-lg ${(selectedItem.quantity_available - parseInt(barsToBook)) <= 0 ? 'text-red-400' : 'text-white'}`}>
                    {selectedItem.quantity_available - parseInt(barsToBook)}
                  </p>
                </div>
              </div>
              {!selectedItem.gradeMatch && (
                <div className="flex items-center gap-2 bg-red-950/50 border border-red-500/30 rounded p-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <p className="text-red-300 text-xs">
                    Material grade does not match. WO requires {requiredGrade}, selected stock is {selectedItem.material_grade}.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep('select')} className="border-slate-600" data-testid="pull-back-btn">
                Back
              </Button>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600">
                  Cancel
                </Button>
                <Button
                  onClick={handleBook}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-500"
                  data-testid="pull-confirm-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  {submitting ? 'Allocating...' : 'Confirm Allocation'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PullFromStockModal;
